import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertCircle, ArrowRight, CalendarClock, ChevronRight, Download, IndianRupee, MapPin, RefreshCw, Search, Wallet } from "lucide-react";
import { EmptyState } from "./common";
import { PaginationBar, useDebounced, usePagedList } from "./PaginationBar";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Input } from "./ui/input";
import { MultiSelect } from "./ui/multi-select";
import { Skeleton } from "./ui/skeleton";
import { downloadBlob, errMsg, fmtDate, fmtINR } from "../lib/api";

/**
 * Phase 105 — Follow-Up list ("top balances due"). Shared by the collector's Follow-Up page /
 * home card and the supervisor's per-collector sheet (Team Scorecard). Highest balance first.
 */

export const QUICK_FILTERS = [
  ["all", "All due"], ["never_paid", "Never paid"], ["due_today", "Due today"], ["overdue", "Overdue follow-up"],
];

/** "Never paid" / "81 days ago (05 Jul 2026)" */
export const lastPaidText = (r) => {
  if (r.never_paid || !r.last_payment_date) return "Never paid";
  const d = r.days_since_last_payment;
  const ago = d == null ? "" : d === 0 ? "today" : d === 1 ? "yesterday" : `${d} days ago`;
  return `Last paid ${ago}${ago ? " · " : ""}${fmtDate(r.last_payment_date)}`;
};

/** Next-visit chip: overdue (red) / due today (amber) / upcoming (neutral). */
export const NextVisitChip = ({ r, testId }) => {
  if (!r.next_visit_date) return null;
  const cls = r.follow_up_overdue ? "bg-destructive/10 text-destructive border-destructive/20"
    : r.follow_up_due_today ? "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning-foreground))] border-[hsl(var(--warning))]/40"
      : "bg-muted text-foreground border-border";
  const label = r.follow_up_overdue ? `Overdue · was ${fmtDate(r.next_visit_date)}`
    : r.follow_up_due_today ? "Visit due today" : `Next visit ${fmtDate(r.next_visit_date)}`;
  return (
    <Badge variant="outline" className={`text-[10px] font-medium gap-1 ${cls}`} data-testid={testId}>
      <CalendarClock className="h-3 w-3" aria-hidden="true" /> {label}
    </Badge>
  );
};

/** One tappable property row (mobile-first card). */
export const FollowUpRow = ({ r, to, rank, testId }) => (
  <Link to={to} className="flex items-stretch gap-3 rounded-lg border bg-card px-3 py-2.5 hover:bg-muted/60 active:scale-[0.995] transition-transform focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    data-testid={testId}>
    {rank != null && (
      <span className="shrink-0 self-center h-7 w-7 rounded-full bg-primary/10 text-primary text-xs font-semibold tabular-nums flex items-center justify-center" aria-hidden="true">{rank}</span>
    )}
    <div className="min-w-0 flex-1">
      <p className="text-sm font-medium truncate">
        <span className="font-mono" data-testid={`${testId}-pin`}>{r.pin}</span>
        <span className="text-muted-foreground font-normal"> • </span>{r.owner_name || "—"}
      </p>
      <p className="text-[11px] text-muted-foreground truncate flex items-center gap-1 mt-0.5">
        <MapPin className="h-3 w-3 shrink-0" aria-hidden="true" />
        <span className="truncate">{[r.address, r.ward_label].filter(Boolean).join(" • ") || "—"}</span>
      </p>
      <div className="flex items-center gap-1.5 flex-wrap mt-1">
        {r.never_paid ? (
          <Badge variant="outline" className="text-[10px] font-medium bg-muted text-muted-foreground" data-testid={`${testId}-never-paid`}>Never paid</Badge>
        ) : (
          <span className="text-[11px] text-muted-foreground" data-testid={`${testId}-last-paid`}>{lastPaidText(r)}</span>
        )}
        <NextVisitChip r={r} testId={`${testId}-next-visit`} />
      </div>
    </div>
    <div className="shrink-0 text-right self-center">
      <p className="text-sm font-semibold tabular-nums text-destructive" data-testid={`${testId}-balance`}>{fmtINR(r.balance_due)}</p>
      <p className="text-[10px] text-muted-foreground">due</p>
    </div>
    <ChevronRight className="h-4 w-4 text-muted-foreground self-center shrink-0" aria-hidden="true" />
  </Link>
);

export const FollowUpSkeleton = ({ rows = 5, testId = "follow-up-skeleton" }) => (
  <div className="space-y-2" aria-hidden="true" data-testid={testId}>
    {Array.from({ length: rows }).map((_, i) => <Skeleton key={i} className="h-[68px] rounded-lg" />)}
  </div>
);

/**
 * Full list with filters + KPIs + pagination.
 * `endpoint`: `/t/{code}/field/follow-up` (me) or `/t/{code}/field/follow-up/{collectorId}` (supervisor/admin).
 * `propertyPath(row)`: where a tap goes.
 */
export function FollowUpList({ endpoint, propertyPath, pageSize = 20, testId = "follow-up", showSearch = true, exportEndpoint, exportName = "follow_up" }) {
  const [ward, setWard] = useState("");
  const [flt, setFlt] = useState("all");
  const [q, setQ] = useState("");
  const [exporting, setExporting] = useState(false);
  const search = useDebounced(q);
  const params = useMemo(() => {
    const p = { with_wards: true };
    if (ward) p.ward_id = ward;
    if (flt && flt !== "all") p.filter = flt;
    if (search) p.search = search;
    return p;
  }, [ward, flt, search]);
  const list = usePagedList(endpoint, { params, initialLimit: pageSize });
  const d = list.data;
  const summary = d?.summary;
  const wards = d?.wards || [];
  const counts = { all: summary?.properties ?? 0, never_paid: summary?.never_paid ?? 0, due_today: summary?.due_today ?? 0, overdue: summary?.overdue ?? 0 };
  const active = ward || flt !== "all" || q;

  const exportExcel = async () => {
    if (!exportEndpoint) return;
    setExporting(true);
    try {
      const qp = new URLSearchParams();
      if (ward) qp.set("ward_id", ward);
      if (flt && flt !== "all") qp.set("filter", flt);
      if (search) qp.set("search", search);
      const qs = qp.toString();
      await downloadBlob(`${exportEndpoint}${qs ? `?${qs}` : ""}`, `${exportName}_${flt}_${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success("Follow-up list exported", { description: `${list.total} propert${list.total === 1 ? "y" : "ies"} with balance due` });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-3" data-testid={testId}>
      <div className="grid grid-cols-2 gap-2" data-testid={`${testId}-kpis`}>
        <Card className="shadow-sm"><CardContent className="p-3">
          <p className="text-[11px] text-muted-foreground flex items-center gap-1"><Wallet className="h-3.5 w-3.5" /> Properties with dues</p>
          <p className="text-xl font-semibold tabular-nums font-heading" data-testid={`${testId}-kpi-count`}>{summary ? summary.properties.toLocaleString("en-IN") : "—"}</p>
        </CardContent></Card>
        <Card className="shadow-sm"><CardContent className="p-3">
          <p className="text-[11px] text-muted-foreground flex items-center gap-1"><IndianRupee className="h-3.5 w-3.5" /> Total balance due</p>
          <p className="text-xl font-semibold tabular-nums font-heading text-destructive" data-testid={`${testId}-kpi-balance`}>{summary ? fmtINR(summary.balance_due) : "—"}</p>
        </CardContent></Card>
      </div>

      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Quick filter" data-testid={`${testId}-quick-filters`}>
          {QUICK_FILTERS.map(([k, l]) => (
            <Button key={k} type="button" size="sm" variant={flt === k ? "default" : "outline"} className="h-8 text-xs rounded-full"
              role="tab" aria-selected={flt === k} onClick={() => setFlt(k)} data-testid={`${testId}-filter-${k}`}>
              {l}{summary ? <span className={`ml-1.5 tabular-nums ${flt === k ? "opacity-80" : "text-muted-foreground"}`}>{counts[k]}</span> : null}
            </Button>
          ))}
        </div>
        {exportEndpoint && (
          <Button type="button" size="sm" variant="outline" className="h-8 text-xs" onClick={exportExcel}
            disabled={exporting || !d || list.total === 0} data-testid={`${testId}-export-button`}>
            <Download className={`h-3.5 w-3.5 mr-1.5 ${exporting ? "animate-pulse" : ""}`} /> {exporting ? "Exporting…" : "Export Excel"}
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {showSearch && (
          <div className="relative flex-1 min-w-[180px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9 h-9" placeholder="Search PIN, owner, address…" value={q} onChange={(e) => setQ(e.target.value)} data-testid={`${testId}-search-input`} />
          </div>
        )}
        <div className="w-48"><MultiSelect options={wards.map((w) => ({ value: w.id, label: w.ward_name }))}
          value={String(ward || "").split(",").filter(Boolean)} onChange={(ids) => setWard(ids.join(","))}
          allLabel="All wards" noun="ward" placeholder="Search wards…" className="h-9" testId={`${testId}-ward-select`} /></div>
      </div>

      {list.error && !d ? (
        <div className="flex flex-col items-center justify-center gap-2 py-10 text-center" data-testid={`${testId}-error`}>
          <AlertCircle className="h-5 w-5 text-destructive" />
          <p className="text-sm text-muted-foreground">Could not load the follow-up list.</p>
          <Button size="sm" variant="outline" onClick={() => list.reload()} data-testid={`${testId}-error-retry`}><RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Retry</Button>
        </div>
      ) : !d ? (
        <FollowUpSkeleton rows={6} testId={`${testId}-skeleton`} />
      ) : d.has_scope === false ? (
        <EmptyState title="No territory or assignments yet" subtitle="Once wards / mohallas are mapped to this collector or properties are assigned, the balances due appear here." />
      ) : list.items.length === 0 ? (
        <EmptyState title={active ? "No properties match" : "Nothing due — great work!"}
          subtitle={active ? "Try another ward or clear the quick filter." : "Every property in your area is fully paid."}
          action={active ? <Button variant="outline" size="sm" onClick={() => { setWard(""); setFlt("all"); setQ(""); }} data-testid={`${testId}-clear-filters`}>Clear filters</Button> : null} />
      ) : (
        <>
          <div className="space-y-2" data-testid={`${testId}-list`}>
            {list.items.map((r, i) => (
              <FollowUpRow key={r.id} r={r} to={propertyPath(r)} rank={(list.page - 1) * list.limit + i + 1} testId={`${testId}-row-${r.pin || r.id}`} />
            ))}
          </div>
          <PaginationBar page={list.page} limit={list.limit} total={list.total} loading={list.loading}
            onPageChange={list.setPage} onLimitChange={list.setLimit} sizes={[10, 20, 50, 100]} testId={`${testId}-pagination`} />
        </>
      )}
    </div>
  );
}

/** Compact "Top balances due" card for the collector home screen. */
export function FollowUpTopCard({ code, limit = 5 }) {
  const list = usePagedList(`/t/${code}/field/follow-up`, { params: {}, initialLimit: limit });
  const d = list.data;
  if (d && (d.has_scope === false || d.total === 0)) return null;
  return (
    <Card className="shadow-sm border-l-4 border-l-destructive/70" data-testid="follow-up-top-card">
      <CardContent className="p-4 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-sm font-heading font-semibold flex items-center gap-2"><Wallet className="h-4 w-4 text-destructive" /> Top balances due</p>
            <p className="text-[11px] text-muted-foreground">
              {d ? <>{d.summary.properties} propert{d.summary.properties === 1 ? "y" : "ies"} owe <span className="font-medium text-foreground tabular-nums">{fmtINR(d.summary.balance_due)}</span> — start here</> : "Where to go first"}
            </p>
          </div>
          <Button asChild variant="ghost" size="sm" className="h-7 text-xs shrink-0" data-testid="follow-up-see-all">
            <Link to={`/${code}/collector/follow-up`}>See all <ArrowRight className="h-3.5 w-3.5 ml-1" /></Link>
          </Button>
        </div>
        {!d ? <FollowUpSkeleton rows={3} testId="follow-up-top-skeleton" /> : (
          <div className="space-y-2">
            {list.items.map((r, i) => <FollowUpRow key={r.id} r={r} rank={i + 1} to={`/${code}/collector/property/${r.id}`} testId={`follow-up-top-row-${r.pin || r.id}`} />)}
          </div>
        )}
        {d && d.summary.overdue + d.summary.due_today > 0 && (
          <p className="text-[11px] text-muted-foreground flex items-center gap-1" data-testid="follow-up-top-due-note">
            <CalendarClock className="h-3 w-3" /> {d.summary.due_today} due today · {d.summary.overdue} overdue follow-up{d.summary.overdue === 1 ? "" : "s"}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
